export const TAB_PERSON = 'person';
export const TAB_PHONE = 'phone';
export const TAB_EMAIL = 'email';
export const TAB_COMPANY = 'company';

const URL_RE = /^https?:\/\//i;

/** Label + TLD (2–6 ASCII letters). Requires at least one dot before TLD. */
const DOMAIN_LABEL = '[a-z0-9](?:[a-z0-9-]*[a-z0-9])?';
const DOMAIN_TLD = '[a-z]{2,6}';
export const STRICT_DOMAIN_RE = new RegExp(
  `^${DOMAIN_LABEL}(?:\\.${DOMAIN_LABEL})*\\.${DOMAIN_TLD}$`,
  'i',
);

const EMAIL_LOCAL = '[^\\s@]+';
const EMAIL_DOMAIN = `${DOMAIN_LABEL}(?:\\.${DOMAIN_LABEL})*\\.${DOMAIN_TLD}`;
const EMAIL_RE = new RegExp(`^${EMAIL_LOCAL}@${EMAIL_DOMAIN}$`, 'i');

const CORPORATE_SUFFIX_RE =
  /\b(?:S\.?\s*R\.?\s*L\.?|S\.?\s*A\.?|INC\.?|LTD\.?|LLC|GMBH|PLC)\b/i;

/**
 * Common given names — single-token queries that should stay Person, not Corporate.
 * Brands like Arcadia / Gazprom / UniCredit / Tesla are NOT in this set.
 */
const COMMON_GIVEN_NAME_RE = new Set(
  [
    'marius',
    'dan',
    'daniel',
    'ion',
    'ioan',
    'andrei',
    'alex',
    'alexandru',
    'maria',
    'ana',
    'ioana',
    'elena',
    'mihai',
    'cristian',
    'adrian',
    'george',
    'vlad',
    'radu',
    'stefan',
    'john',
    'jane',
    'michael',
    'david',
    'james',
    'robert',
    'mary',
    'anna',
    'paul',
    'peter',
    'thomas',
    'mark',
    'luke',
    'sarah',
    'emma',
  ].map((n) => n.toLowerCase()),
);

/** Strip phone formatting characters before digit-only check. */
const PHONE_NOISE_RE = /[\s\-().+/]/g;

export const SEARCH_ACTION_BY_TAB = {
  [TAB_PERSON]: 'Find Person',
  [TAB_EMAIL]: 'Analyze Email',
  [TAB_PHONE]: 'Trace Phone',
  [TAB_COMPANY]: 'Investigate Company',
};

export const DETECTION_CHIP_BY_TAB = {
  [TAB_PERSON]: { label: 'Person', hint: 'Name ± city/employer; :: for context' },
  [TAB_EMAIL]: { label: 'Email', hint: 'Email address pattern' },
  [TAB_PHONE]: { label: 'Phone', hint: 'Numeric phone format' },
  [TAB_COMPANY]: { label: 'Corporate', hint: 'domain · location · context' },
};

export const CORPORATE_OVERRIDE_WARNING =
  'Due Diligence works best with a valid website domain (e.g. company.ro).';

/**
 * Shown when Corporate is auto-selected (or active) without a resolvable domain.
 * Prefer this over a blocking modal so the user can keep typing.
 */
export const CORPORATE_DOMAIN_ACCURACY_HINT =
  'For more accurate Due Diligence, add a valid website domain next to the company name (e.g. Arcadia arcadiamedical.ro).';

/** Blocks Corporate GO / launch until a valid domain is present (query or pill). */
export const CORPORATE_DOMAIN_REQUIRED_HINT =
  'Add a valid domain to enable searching';

/**
 * Blocks Person / Corporate GO when the query is a single insufficient term
 * (not email, phone, URL, domain, or username handle).
 */
export const SINGLE_TERM_INSUFFICIENT_HINT =
  'Need more detail — full name or company + domain (± location/context), email, or phone. Pick a module below.';

/** Soft banner when email or corporate domain does not resolve (typo guard — does not use a modal). */
export const EMAIL_DOMAIN_DNS_HINT =
  'Scan not started — the domain does not resolve in DNS. Check for a typo in the address.';

export const CORPORATE_DOMAIN_DNS_HINT = EMAIL_DOMAIN_DNS_HINT;

/** @typedef {{ tab: string, requiresDomain?: boolean }} DetectionResult */

export function extractHostFromQuery(raw) {
  const q = String(raw || '').trim();
  if (!q) return '';
  const withoutProto = q.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
  const firstSegment = withoutProto.split(/[/?#]/)[0].trim().toLowerCase();
  if (isValidDomainHost(firstSegment)) return firstSegment;
  return findDomainInQuery(q);
}

/** First valid domain token in free text (e.g. "arcadiamedical.ro Arcadia Hospital Iasi"). */
export function findDomainInQuery(raw) {
  const q = String(raw || '').trim();
  if (!q) return '';

  const tokens = q.split(/\s+/);
  for (const token of tokens) {
    const cleaned = token
      .replace(/^[(["']+/, '')
      .replace(/[.,;:!?)["']+$/, '')
      .trim();
    if (!cleaned) continue;
    const withoutProto = cleaned.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
    const host = withoutProto.split(/[/?#]/)[0].trim().toLowerCase();
    if (isValidDomainHost(host)) return host;
  }
  return '';
}

export function isValidDomainHost(host) {
  const h = String(host || '').trim().toLowerCase();
  if (!h || h.includes('@')) return false;
  return STRICT_DOMAIN_RE.test(h);
}

export function looksLikeEmail(raw) {
  const q = String(raw || '').trim();
  if (!q.includes('@')) return false;
  return EMAIL_RE.test(q);
}

export function hasCorporateLegalSuffix(raw) {
  return CORPORATE_SUFFIX_RE.test(String(raw || ''));
}

/**
 * Social / OSINT handles: ``avmarius``, ``@avmarius``, ``user_123`` — not corporate brands.
 *
 * @param {string} raw
 * @returns {boolean}
 */
export function looksLikeUsernameHandle(raw) {
  const q = String(raw || '').trim();
  if (!q || URL_RE.test(q) || /\s/.test(q)) return false;
  const token = q.replace(/^@+/, '');
  if (!token || token.length < 3) return false;
  if (!/^[\p{L}\p{N}_.-]+$/u.test(token)) return false;
  if (isValidDomainHost(token.replace(/^www\./i, ''))) return false;
  if (hasCorporateLegalSuffix(token)) return false;
  const lower = token.toLowerCase();
  if (/[0-9_]/.test(lower)) return true;
  for (const name of COMMON_GIVEN_NAME_RE) {
    if (lower.length > name.length && lower.includes(name)) return true;
  }
  return false;
}

/**
 * Bare org/brand heuristic (legacy). Single alphabetic tokens must NOT auto-route
 * to Corporate — that allowed ``Victoria`` / ``Grindeanu`` to flip tabs while
 * ``Marius`` stayed Person. Kept exported for tests; ``detectInput`` no longer uses it.
 *
 * @param {string} raw
 * @returns {boolean}
 */
export function looksLikeBareOrganizationQuery(raw) {
  const q = String(raw || '').trim();
  if (!q || URL_RE.test(q) || q.includes('@')) return false;
  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length !== 1) return false;
  const token = tokens[0].replace(/^[(["']+|[,.;:!?)\]"']+$/g, '');
  if (!token || token.length < 3) return false;
  if (looksLikeUsernameHandle(token)) return false;
  if (isValidDomainHost(token.replace(/^www\./i, ''))) return false;
  if (hasCorporateLegalSuffix(token)) return false;
  if (COMMON_GIVEN_NAME_RE.has(token.toLowerCase())) return false;
  // Prefer letter-led brand tokens (Latin + RO diacritics).
  if (!/^[\p{L}][\p{L}\d&'.-]*$/u.test(token)) return false;
  return true;
}

/**
 * True when Person / Corporate must not start: exactly one token that is not
 * email, phone, URL, domain host, or an explicit username handle
 * (``@avmarius``, ``user_123``, compound ``avmarius`` — not bare given names).
 *
 * @param {string} raw
 * @returns {boolean}
 */
export function isInsufficientSingleTermQuery(raw) {
  const q = String(raw || '').trim();
  if (!q) return false;
  if (looksLikeEmail(q) || looksLikePhoneQuery(q)) return false;
  if (URL_RE.test(q)) return false;
  if (isWholeQueryDomain(q) || queryContainsValidDomain(q)) return false;
  if (isAllowedSingleTermUsernameHandle(q)) return false;
  const tokens = q.split(/\s+/).filter(Boolean);
  return tokens.length === 1;
}

/**
 * Username / handle exception for the single-term gate.
 * Bare given names (``Alexandru``, ``Marius``) must stay gated even when they
 * falsely match ``looksLikeUsernameHandle`` via an embedded shorter name.
 *
 * @param {string} raw
 * @returns {boolean}
 */
function isAllowedSingleTermUsernameHandle(raw) {
  const q = String(raw || '').trim();
  if (!looksLikeUsernameHandle(q)) return false;
  const token = q.replace(/^@+/, '').toLowerCase();
  if (q.startsWith('@') || /[0-9_]/.test(token)) return true;
  // Compound handle: given-name fragment embedded but not the whole / prefix token.
  for (const name of COMMON_GIVEN_NAME_RE) {
    if (token === name) return false;
    if (token.startsWith(name)) return false;
    if (token.length > name.length && token.includes(name)) return true;
  }
  return false;
}

const COMMERCIAL_BRAND_TOKENS = new Set([
  'catalog',
  'web',
  'medical',
  'medicala',
  'clinic',
  'clinica',
  'hospital',
  'spital',
  'group',
  'grup',
  'solutions',
  'consulting',
  'service',
  'servicii',
  'shop',
  'store',
  'telecom',
  'bank',
  'banca',
  'credit',
  'imobiliare',
  'transport',
  'logistic',
  'hotel',
  'restaurant',
  'pharmacy',
  'farmacie',
  'media',
  'digital',
  'systems',
  'network',
]);

/**
 * Multi-token commercial brand (e.g. Catalog Web, Arcadia Medical) → Corporate.
 * @param {string} raw
 * @returns {boolean}
 */
export function looksLikeCommercialBrandQuery(raw) {
  const q = String(raw || '').trim();
  if (!q || URL_RE.test(q) || q.includes('@') || looksLikePhoneQuery(q) || looksLikeEmail(q)) {
    return false;
  }
  if (findDomainInQuery(q) || hasCorporateLegalSuffix(q)) return false;
  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length < 2 || tokens.length > 4) return false;
  const lows = tokens.map((t) => t.toLowerCase().replace(/[.,;:!?]+$/g, ''));
  if (!lows.some((t) => COMMERCIAL_BRAND_TOKENS.has(t))) return false;
  // Avoid "Ion Popescu" style: no commercial token usually; if first looks like a given name and no brand token beyond weak ones, skip.
  if (COMMON_GIVEN_NAME_RE.has(lows[0]) && !lows.slice(1).some((t) => COMMERCIAL_BRAND_TOKENS.has(t))) {
    return false;
  }
  return true;
}

/**
 * True when the trimmed query is a single valid domain label (no spaces).
 * @param {string} raw
 * @returns {boolean}
 */
function isWholeQueryDomain(raw) {
  const q = String(raw || '').trim();
  if (!q || URL_RE.test(q) || /\s/.test(q)) return false;
  const host = q.replace(/^www\./i, '').split(/[/?#]/)[0].trim().toLowerCase();
  return isValidDomainHost(host);
}

/**
 * True when the query contains any resolvable valid domain (URL, bare, or embedded token).
 * @param {string} raw
 * @returns {boolean}
 */
function queryContainsValidDomain(raw) {
  const q = String(raw || '').trim();
  if (!q) return false;
  const host = extractHostFromQuery(q);
  if (URL_RE.test(q) && isValidDomainHost(host)) return true;
  if (isWholeQueryDomain(q)) return true;
  return Boolean(findDomainInQuery(q));
}

/**
 * Phone heuristic: 9–15 digits after stripping allowed separators; no letters or @ in original.
 * Aligned with ``queryValidator.looksLikePhone`` (incomplete digit soup is not phone).
 * @param {string} raw
 * @returns {boolean}
 */
function looksLikePhoneQuery(raw) {
  const q = String(raw || '').trim();
  if (!q || /[a-zA-Z@]/.test(q)) return false;
  const digitsOnly = q.replace(PHONE_NOISE_RE, '');
  return /^\d{9,15}$/.test(digitsOnly);
}

/**
 * Deterministic home-bar module detection (priority: email → phone → corporate → person).
 *
 * @param {string} query - Raw hero search text.
 * @returns {DetectionResult} Resolved tab and optional corporate domain requirement flag.
 */
export function detectInput(query) {
  const q = String(query ?? '').trim();

  if (!q) {
    return { tab: TAB_PERSON, requiresDomain: false };
  }

  if (looksLikeEmail(q)) {
    return { tab: TAB_EMAIL, requiresDomain: false };
  }

  if (looksLikePhoneQuery(q)) {
    return { tab: TAB_PHONE, requiresDomain: false };
  }

  if (looksLikeUsernameHandle(q)) {
    return { tab: TAB_PERSON, requiresDomain: false };
  }

  const host = extractHostFromQuery(q);
  if (URL_RE.test(q) && isValidDomainHost(host)) {
    return { tab: TAB_COMPANY, requiresDomain: false };
  }

  if (isWholeQueryDomain(q)) {
    return { tab: TAB_COMPANY, requiresDomain: false };
  }

  if (findDomainInQuery(q)) {
    return { tab: TAB_COMPANY, requiresDomain: false };
  }

  if (hasCorporateLegalSuffix(q) && !queryContainsValidDomain(q)) {
    return { tab: TAB_COMPANY, requiresDomain: true };
  }

  // Multi-word commercial brands (Catalog Web, Arcadia Medical) → Corporate.
  // Single alphabetic tokens stay Person (gated by isInsufficientSingleTermQuery).
  if (looksLikeCommercialBrandQuery(q)) {
    return { tab: TAB_COMPANY, requiresDomain: !findDomainInQuery(q) };
  }

  return { tab: TAB_PERSON, requiresDomain: false };
}

/** @returns {string} */
export function detectInputType(raw) {
  return detectInput(raw).tab;
}

export function inferTabFromQuery(raw) {
  return detectInputType(raw);
}

export function companyFieldHintForQuery(q) {
  if (findDomainInQuery(q)) return 'domain';
  const host = extractHostFromQuery(q);
  if (isValidDomainHost(host) && !String(q || '').includes('@')) return 'domain';
  return 'name';
}

export function validateCompanyDomainSyntax(raw) {
  const host = findDomainInQuery(raw) || extractHostFromQuery(raw);
  if (!host) return { valid: false, host: '', message: 'Enter a valid domain (e.g. company.ro).' };
  if (!isValidDomainHost(host)) {
    return {
      valid: false,
      host,
      message: 'The domain must include a valid TLD (2–6 letters), e.g. .ro or .com.',
    };
  }
  return { valid: true, host, message: '' };
}

/** Domain host from a corporate hero query, when present and syntactically valid. */
export function companyDomainFromQuery(raw) {
  const host = findDomainInQuery(raw) || validateCompanyDomainSyntax(raw).host;
  return host && isValidDomainHost(host) ? host : '';
}

export function searchActionLabel(tab) {
  if (!tab) return 'Search';
  return SEARCH_ACTION_BY_TAB[tab] || 'Search';
}

/**
 * English UX copy when the hero query looks like a shell/SQL/code paste.
 * Kept in sync with backend ``validate_search_intent`` rejection intent.
 */
export const SYSTEM_COMMAND_MESSAGE =
  'System command or invalid syntax detected. Enter a name, company, email, or phone number.';

const SHELL_OR_SERVICE_RE =
  /\b(?:net\s+(?:stop|start|use|user|share|localgroup|view|session)|taskkill|tasklist|ipconfig|ifconfig|systemctl|sudo|docker(?:\s+compose)?\s+(?:up|down|build|run|ps|pull|stop|start|rm|exec|logs)|kubectl|curl\s+(?:-|https?:)|wget\s+|chmod|chown|rm\s+-rf|apt(?:-get)?\s+(?:install|remove|update)|yum\s+(?:install|remove)|powershell|cmd\.exe|sc\s+(?:stop|start|query)|killall|pkill)\b/i;

const SQL_SNIPPET_RE =
  /\b(?:select\b[\s\S]{0,200}\bfrom\b|insert\s+into\b|delete\s+from\b|drop\s+(?:table|database|schema)\b|update\b[\s\S]{0,80}\bset\b|union\s+select\b)/i;

const CODE_SNIPPET_RE =
  /\b(?:import\s+os\b|from\s+\w+\s+import\b|require\s*\(|console\.(?:log|error|warn)\s*\(|eval\s*\(|function\s*\(|def\s+\w+\s*\(|class\s+\w+\s*[(:])|<\s*script\b|<\/\s*script\s*>|#!\/bin\/(?:ba)?sh\b/i;

/**
 * True when free-text looks like a shell command, SQL, or code paste — not an OSINT target.
 *
 * @param {unknown} raw
 * @returns {boolean}
 */
export function looksLikeSystemCommandOrCode(raw) {
  const text = String(raw || '').trim();
  if (!text) return false;
  return (
    SHELL_OR_SERVICE_RE.test(text) ||
    SQL_SNIPPET_RE.test(text) ||
    CODE_SNIPPET_RE.test(text)
  );
}
